import { Link } from 'react-router';
import { publicApi, type ApiReference } from '@p1plug/platform';
import { DataTable } from '@p1plug/ui';
import { C, Code, DocPage, H2, P, Snippet } from '../doc';
import { firstRequest, token, tokenResponse } from '../samples';

const api = publicApi as ApiReference;

export function Authentication() {
  return (
    <DocPage
      title="Authentication"
      lead="Every request carries a bearer credential that identifies your organisation. There are two kinds; both end up in the same header."
    >
      <DataTable
        rowKey={(r) => r.kind}
        columns={[
          { key: 'kind', label: 'Credential' },
          { key: 'looks', label: 'Looks like', mono: true },
          { key: 'use', label: 'Use it for' },
          { key: 'scope', label: 'Permissions' },
        ]}
        rows={[
          {
            kind: 'API key',
            looks: 'p1k_…',
            use: 'Development and your first integration',
            scope: 'Everything your organisation may do',
          },
          {
            kind: 'OAuth access token',
            looks: 'eyJ… (JWT, 1 hour)',
            use: 'Production, server to server',
            scope: 'Only the scopes of the machine client that requested it',
          },
        ]}
      />

      <H2>API key</H2>
      <P>Send the key as a bearer token. It does not expire; treat it like a password and rotate it if it leaks.</P>
      <Code samples={firstRequest} />

      <H2>OAuth 2.0 client credentials</H2>
      <P>
        A machine client is an identity for one of your services, with a client ID (<C>p1c_…</C>), a secret and a list
        of allowed scopes. The service exchanges them for a short-lived access token and sends that token as the bearer.
      </P>
      <Code samples={token} />
      <Snippet title="200 OK" code={tokenResponse} />
      <P>
        Tokens are ES256-signed JWTs. You never need to inspect one, but you can verify it with the public keys at{' '}
        <C>/.well-known/jwks.json</C>. Request a new token when the old one is about to expire; there are no refresh
        tokens in this flow.
      </P>

      <H2>Scopes</H2>
      <P>
        Give each service the least it needs: the service that places orders does not need to read telemetry. An
        operation without the required scope answers <C>403 insufficient_scope</C>.
      </P>
      <DataTable
        rowKey={(r) => r.scope}
        columns={[
          { key: 'scope', label: 'Scope', mono: true },
          { key: 'allows', label: 'Allows' },
        ]}
        rows={Object.entries(api.scopes).map(([scope, allows]) => ({ scope, allows }))}
      />
      <P>
        The required scope is listed with every operation in the <Link to="/reference">API reference</Link>.
      </P>

      <H2>What your organisation can see</H2>
      <P>
        A credential only ever reaches your own organisation’s resources. A resource of another organisation answers{' '}
        <C>404</C>, exactly as if it did not exist, so identifiers cannot be probed. The one exception is data a
        consumer chose to share with you: it appears as a meter point with <C>access: "granted"</C> and carries
        telemetry only.
      </P>

      <H2>When a credential leaks</H2>
      <ol className="p1x-prose">
        <li>Ask P1Plug to rotate the client secret or revoke the key. The old secret stops working immediately.</li>
        <li>Deploy the new secret. Tokens already issued expire within the hour.</li>
        <li>
          Check <Link to="/account/usage">API usage</Link> for requests you do not recognise.
        </li>
      </ol>
    </DocPage>
  );
}
